<template>
  <div class="table-container">
    <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <thead class="table-header">
        <tr>
          <th v-for="data in isAdmin ? adminHeaders : headers" :key="data" class="table-header-item">{{ data }}</th>
        </tr>
      </thead>

      <tbody class="table-content">
        <tr v-for="item in tableValues" :key="item.id" class="table-line">
          <td v-if="!isAdmin" class="table-item monitoring">
            <MonitoringType :level="item.level" :label="item.label" :config="{ maxElementWidth: 174 }" />
          </td>

          <td v-if="!isAdmin" class="table-item package">
            <div class="item">{{ item.package.name }}</div>
            <div class="item blue">{{ item.package.description }}</div>
          </td>

          <td class="table-item">
            <div class="item">{{ $utils.formatDate(item.createdAt) }}</div>
          </td>

          <td v-if="!isAdmin" class="table-item">
            <div class="item">{{ !item.isEnabled ? $utils.formatDate(item.updatedAt) : '-' }}</div>
          </td>

          <td v-if="isAdmin" class="table-item">
            <div class="item">{{ item.name || '-' }}</div>
          </td>

          <td class="table-item">
            <div class="item">{{ item.package ? item.package.pricePm : item.pricePm || '-' }}</div>
          </td>
          <td class="table-item">
            <div class="item">{{ item.package ? item.package.pricePp : item.pricePp || '-' }}</div>
          </td>

          <td v-if="!isAdmin" class="table-item edit">
            <div v-if="item.level !== 0 && item.isEnabled" class="item">
              <span class="link-button" @click="() => edit(item)">{{ $t('change') }}</span>
            </div>
            <div v-else class="item">
              <span class="link-button disabled">{{ item.isEnabled ? $t('noEditable') : $t('disabled') }}</span>
            </div>
          </td>
          <td v-else class="table-item edit admin">
            <div v-if="item.level !== 0" class="item">
              <span class="link-button" @click="() => edit(item)">{{ $t('change') }}</span>
            </div>
            <div v-if="!item.isEnabled" class="item">
              <span class="link-button disabled">{{ $t('invisiblePackage') }}</span>
            </div>
          </td>
        </tr>

        <tr v-if="!tableValues.length" class="table-line empty">
          <td class="table-item" colspan="5">
            <div class="item">
              <div v-if="loading" class="lds-ring-grey">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              {{ loading ? $t('searchInProgress') : $t('noCompanies') }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="table-container show-medium">
      <div v-for="item in tableValues" :key="item.id" class="table-line">
        <div class="line">
          <div class="line-content">
            <MonitoringType :level="item.level" :label="item.label" />
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('packageDocuments') }}
          </div>
          <div v-if="!isAdmin" class="line-content">
            <div class="item">{{ item.package.name }}</div>
            <div class="item blue">{{ item.package.description }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('creationDate') }}
          </div>
          <div class="line-content">
            <div class="item">{{ $utils.formatDate(item.createdAt) }}</div>
          </div>
        </div>

        <div v-if="!isAdmin" class="line column">
          <div class="line-content title">
            {{ $t('disableDate') }}
          </div>
          <div class="line-content">
            <div class="item">{{ !item.isEnabled ? $utils.formatDate(item.updatedAt) : '-' }}}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('packagePriceMoralPerson') }}
          </div>
          <div class="line-content">
            <div class="item">{{ item.pricePm || '-' }}</div>
          </div>
        </div>
        <div class="line column">
          <div class="line-content title">
            {{ $t('packagePricePhysicPerson') }}
          </div>
          <div class="line-content">
            <div class="item">{{ item.pricePp || '-' }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content">
            <div class="item" @click="() => edit(item)">
              <span class="link-button">{{ $t('change') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Pagination v-if="values && values.length" :pagination="pagination" :set-page="setPage" :tableValues="values" :pages="pages" :disable-dots="disableDots" />
  </div>
</template>

<script>
import MonitoringType from '@/components/monitoringType';
import Pagination from '@/components/Pagination';
import tableMixin from '@/components/tables/TableMixin';

export default {
  components: { Pagination, MonitoringType },
  mixins: [tableMixin],

  props: {
    isAdmin: {
      type: Boolean,
      required: false,
      default: () => false,
    },

    setCount: {
      type: Function,
      required: false,
      default: () => false,
    },
  },

  data() {
    return {
      adminHeaders: [this.$t('creationDate'), this.$t('packageLabel'), this.$t('packagePriceMoralPerson'), this.$t('packagePricePhysicPerson'), ''],
      headers: [
        this.$t('monitoringLevel'),
        this.$t('packageDocuments'),
        this.$t('creationDate'),
        this.$t('disableDate'),
        this.$t('packagePriceMoralPerson'),
        this.$t('packagePricePhysicPerson'),
        '',
      ],
      values: [],
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.getData,
        maxElement: 5,
      },
      loading: true,
    };
  },

  methods: {
    edit(item) {
      if (this.isAdmin) {
        this.$router.push({
          name: 'adminPackageEdit',
          query: {
            id: item.publicId,
            name: item.name,
            pricePm: item.pricePm,
            pricePp: item.pricePp,
            code: item.code,
            checkedDocuments: item.value,
            companyId: item.clientPublicId,
            isEnabled: item.isEnabled,
          },
        });
      } else {
        this.$router.push({
          name: 'accountEdit',
          query: {
            id: item.publicId,
            packageId: item.package.publicId,
            label: item.label,
            level: item.level,
            isEnabled: item.isEnabled,
          },
        });
      }
    },

    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      let data;
      if (this.isAdmin) {
        data = await this.$store.dispatch('getPackages', { pageNumber, pageSize });
      } else {
        data = await this.$store.dispatch('getPackagesLevel', { pageNumber, pageSize });
      }
      this.setCount(data.count);
      this.values = data.items;
      this.pagination.totalLength = data.count;
      this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
      return { data, values: data.items };
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.table-container {
  .header {
    margin: 60px 0 0;
    display: flex;
    justify-content: space-between;

    .title {
      display: flex;
      align-items: flex-end;
      font-weight: 500;
      font-size: 20px;
    }

    .main-button {
      max-width: 200px;
      padding: 0;
    }
  }
}

.table .table-content .table-line {
  background: colors.$white-color;

  &.empty .table-item .item {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: colors.$lightgrey-color;
    font-size: 14px;
    font-style: italic;

    .lds-ring-grey {
      margin-right: 24px;
    }
  }

  &:last-child .table-item {
    border: none;
  }

  .table-item {
    border-bottom: 1px solid colors.$border-color;
    background: transparent;
    box-sizing: border-box;
    height: 100px !important;
    overflow: hidden;

    &.monitoring {
      max-width: 196px;
    }

    &.edit {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      &.admin {
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;

        .item {
          margin: 4px;
        }
      }
    }

    &.package {
      .item {
        color: colors.$lightgrey-color;
        font-size: 12px;

        &.blue {
          color: colors.$lightblue-color;
        }
      }
    }

    .item {
      color: colors.$darkgrey-color;
      font-size: 13px;
      font-weight: normal;
      display: flex;
      justify-content: space-between;

      &.bold {
        font-weight: 500;
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;

        margin: 12px 0;

        &.column {
          flex-direction: column;
          justify-content: space-evenly;
          align-items: flex-start;
        }

        .line-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          &.title {
            font-weight: 500;
            color: colors.$grey-color;
            line-height: 16px;
            font-size: 14px;
            margin-bottom: 8px;
          }

          .item {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
        }
      }
    }
  }
}
</style>
